import axios, { AxiosInstance, RawAxiosRequestHeaders } from 'axios';

import { ERROR_LEVELS, SENTRY_AXIOS } from 'services/Sentry/constants';
import { UNAUTHORIZED_TOKEN, UNAUTHORIZED_USER } from './constants';

import sentry from 'services/Sentry/SentryInstance';

import { getToken, setToken, removeToken } from 'api/utils/tokenManagement';
import { getLanguage } from 'api/utils/languageManagment';

const PLATFORM_CODE = 3;

export type ApiErrorHandler = (errorData: {
    extras?: Record<string, unknown>;
    status?: number;
    message: string;
    error: any;
}) => void;

interface ConstructorParams {
    apiUrl?: string;
    apiVersion?: string;
    apiKey?: string;
    isPrivate?: boolean;
    onError?: ApiErrorHandler;
    retries?: number;
    timeout?: number;
    requestInterceptors?: Array<(config: any) => any>;
    getAuthToken?: () => string | null | undefined;
    onUpdateToken?: (token: string) => void;
    onAuthorisationError?: (error: any) => void;
}

interface IRequestOptions<RequestData> {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data?: Partial<RequestData>;
    params?: Record<string, string>;
    headers?: Record<string, string>;
}

export default class ApiMethods {
    apiUrl: string;
    apiVersion: string;
    apiKey: string;
    isPrivate: boolean;
    instance: AxiosInstance;
    requiredHeaders: string[];
    onError?: ApiErrorHandler;
    retries: number;
    timeout: number;
    requestInterceptors: Array<(config: any) => any>;
    getAuthToken: () => string | null | undefined;
    onUpdateToken?: (token: string) => void;
    onAuthorisationError?: (error: any) => void;

    constructor({
        apiUrl = import.meta.env.VITE_WF_API_URL,
        apiVersion = import.meta.env.VITE_API_VERSION,
        apiKey = import.meta.env.VITE_WF_API_KEY,
        isPrivate = false,
        onError,
        retries = 3,
        timeout = 35000,
        requestInterceptors = [],
        getAuthToken = getToken,
        onUpdateToken,
        onAuthorisationError,
    }: ConstructorParams) {
        this.apiUrl = apiUrl;
        this.apiVersion = apiVersion;
        this.apiKey = apiKey;
        this.isPrivate = isPrivate;
        this.requiredHeaders = ['version', 'x-api-key', 'language', 'platform'];
        this.onError = onError;
        this.retries = retries;
        this.timeout = timeout;
        this.requestInterceptors = requestInterceptors;
        this.getAuthToken = getAuthToken;
        this.onUpdateToken = onUpdateToken;
        this.onAuthorisationError = onAuthorisationError;
        this.instance = this.createAxiosInstance();
    }

    createAxiosInstance() {
        const axiosInstance: AxiosInstance = axios.create({
            baseURL: this.apiUrl,
            timeout: this.timeout,
        });

        axiosInstance.defaults.headers.common['version'] = this.apiVersion;
        axiosInstance.defaults.headers.common['x-api-key'] = this.apiKey;
        axiosInstance.defaults.headers.common['language'] = getLanguage();
        axiosInstance.defaults.headers.common['platform'] = PLATFORM_CODE;

        // Apply custom request interceptors
        this.requestInterceptors.forEach((interceptor) => {
            axiosInstance.interceptors.request.use(interceptor);
        });

        return axiosInstance;
    }

    createTmpOptions<RequestData>(options: IRequestOptions<RequestData>, token: string | null | undefined) {
        const tmpOptions: any = { ...options, url: `/${options.url}` };

        tmpOptions.params = tmpOptions.params || {};
        tmpOptions.headers = {
            ...tmpOptions.headers,
            'Content-Type': 'application/json',
            ...(token && this.isPrivate && { token }),
        };

        return tmpOptions;
    }

    headerIsMissing(common: RawAxiosRequestHeaders) {
        return this.requiredHeaders.some((header) => !common[header]);
    }

    async get<ResponseData>(url: string, params?: Record<string, string>, headers?: Record<string, string>) {
        return this.request<unknown, ResponseData>({ url, params, headers, method: 'GET', data: {} });
    }

    async post<RequestData, ResponseData>(url: string, payload: Partial<RequestData> = {}) {
        return this.request<RequestData, ResponseData>({ url, method: 'POST', data: payload });
    }

    async put<RequestData, ResponseData = never>(url: string, payload: Partial<RequestData> = {}) {
        return this.request<RequestData, ResponseData>({ url, method: 'PUT', data: payload });
    }

    async patch<RequestData, ResponseData>(url: string, payload: Partial<RequestData> = {}) {
        return this.request<RequestData, ResponseData>({ url, method: 'PATCH', data: payload });
    }

    async delete<RequestData, ResponseData = never>(url: string, payload: Partial<RequestData> = {}) {
        return this.request<RequestData, ResponseData>({ url, method: 'DELETE', data: payload });
    }

    async request<RequestData, ResponseData = never>(options: IRequestOptions<RequestData>): Promise<ResponseData> {
        const token = this.getAuthToken();

        if (!token && this.isPrivate) {
            sentry.logError(new Error('Token is required for private method'), SENTRY_AXIOS, ERROR_LEVELS.CRITICAL, {
                details: options,
            });
        }

        this.instance.defaults.headers.common['language'] = getLanguage();

        if (token) this.instance.defaults.headers.common['token'] = token;

        const { common } = this.instance.defaults.headers;

        if (this.headerIsMissing(common)) {
            sentry.logError(new Error('Required header is missing'), SENTRY_AXIOS, ERROR_LEVELS.CRITICAL, {
                ...common,
            });

            return Promise.reject(new Error('Required header is missing'));
        }

        const tmpOptions = this.createTmpOptions(options, token);

        try {
            const response = await this.instance(tmpOptions);

            if (response.headers.token) {
                const newToken = response.headers.token;
                if (this.onUpdateToken) {
                    this.onUpdateToken(newToken);
                } else {
                    setToken(newToken);
                }
                response.data.token = newToken;
            }
            if (response.headers.country) response.data.country = response.headers.country;

            return Promise.resolve(response.data);
        } catch (error: any) {
            const errorResponse = error?.response;
            const errorStatus = errorResponse?.status;

            // Call custom error handler
            if (this.onError) {
                this.onError({
                    extras: { options: tmpOptions, response: errorResponse },
                    status: errorStatus,
                    message: error?.message || 'API request failed',
                    error,
                });
            }

            // Call custom authorization error handler
            if (this.isPrivate && errorStatus && [UNAUTHORIZED_USER, UNAUTHORIZED_TOKEN].includes(errorStatus)) {
                if (this.onAuthorisationError) {
                    this.onAuthorisationError(error);
                } else {
                    removeToken();
                    window.location.href = '/login';
                }
            }

            if (axios.isAxiosError(error)) {
                sentry.logError(new Error('Axios error'), SENTRY_AXIOS, ERROR_LEVELS.ERROR, {
                    config: error?.config,
                    options: tmpOptions,
                    response: errorResponse,
                });
            } else {
                sentry.logError(new Error('Untracked error'), SENTRY_AXIOS, ERROR_LEVELS.CRITICAL, {
                    options: tmpOptions,
                    response: errorResponse,
                });
            }

            return Promise.reject(error);
        }
    }
}
